<script lang="ts">
	import type { ApexOptions } from 'apexcharts';
	import type { MetricSeries } from '../../AppTypes';
	import Chart from '../Chart.svelte';

	export let series: MetricSeries;

	// String-valued metrics are plotted as a step chart over their distinct values
	// (a state timeline), with the categories on the y-axis.
	$: isNumeric = series.points.every((p) => typeof p.value === 'number');
	$: changes = series.points.reduce<{ t: string; value: number | string }[]>((acc, p) => {
		if (acc.length === 0 || acc[acc.length - 1].value !== p.value) {
			acc.push({ t: p.t, value: p.value });
		}
		return acc;
	}, []);

	// Distinct string values, in order of first appearance → y-axis categories.
	$: categories = [...new Set(series.points.map((p) => String(p.value)))];
	$: categoryAxisMax = Math.max(categories.length - 1, 1);
	$: categoryChartOptions = {
		chart: {
			type: 'line',
			height: 200,
			fontFamily: 'inherit',
			foreColor: '#94a3b8',
			toolbar: { show: false },
			zoom: { enabled: false },
			animations: { enabled: false }
		},
		series: [
			{
				name: series.label,
				data: series.points.map((p) => ({ x: new Date(p.t).getTime(), y: categories.indexOf(String(p.value)) }))
			}
		],
		colors: ['#6366f1'],
		dataLabels: { enabled: false },
		stroke: { curve: 'stepline', width: 2 },
		markers: { size: 3 },
		grid: { borderColor: 'rgba(148,163,184,0.2)', strokeDashArray: 4 },
		xaxis: { type: 'datetime', labels: { datetimeUTC: false }, axisBorder: { show: false }, axisTicks: { show: false } },
		yaxis: {
			min: 0,
			max: categoryAxisMax,
			tickAmount: categoryAxisMax,
			forceNiceScale: false,
			labels: { formatter: (v: number) => categories[Math.round(v)] ?? '' }
		},
		tooltip: {
			theme: 'dark',
			x: { format: 'MMM dd, HH:mm' },
			y: { formatter: (v: number) => categories[Math.round(v)] ?? '' }
		}
	} as ApexOptions;

	$: chartOptions = {
		chart: {
			type: 'area',
			height: 200,
			fontFamily: 'inherit',
			foreColor: '#94a3b8',
			toolbar: { show: false },
			zoom: { enabled: false },
			animations: { enabled: false }
		},
		series: [
			{
				name: series.label,
				data: series.points.map((p) => ({ x: new Date(p.t).getTime(), y: p.value as number }))
			}
		],
		colors: ['#3b82f6'],
		dataLabels: { enabled: false },
		stroke: { curve: 'smooth', width: 2 },
		fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 100] } },
		grid: { borderColor: 'rgba(148,163,184,0.2)', strokeDashArray: 4 },
		markers: { size: 0, hover: { size: 4 } },
		xaxis: { type: 'datetime', labels: { datetimeUTC: false }, axisBorder: { show: false }, axisTicks: { show: false } },
		yaxis: {
			// Only include `title` when there's a unit — passing `title: undefined`
			// makes ApexCharts throw (it reads title.text internally).
			...(series.unit ? { title: { text: series.unit, style: { fontWeight: 500 } } } : {}),
			labels: { formatter: (v: number) => `${Math.round(v * 100) / 100}` }
		},
		tooltip: {
			theme: 'dark',
			x: { format: 'MMM dd, HH:mm' },
			y: { formatter: (v: number) => `${v}${series.unit ? ' ' + series.unit : ''}` }
		}
	} as ApexOptions;
</script>

<div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
	<div class="flex items-center justify-between mb-2">
		<div>
			<span class="font-medium text-gray-900 dark:text-white">{series.label}</span>
			<span class="text-xs text-gray-400 ml-2">{series.key}{series.unit ? ` (${series.unit})` : ''}</span>
		</div>
	</div>

	{#if series.points.length === 0}
		<p class="text-sm text-gray-400 py-6 text-center">No data for this metric in the selected range.</p>
	{:else if !isNumeric}
		<Chart options={categoryChartOptions} />
		<p class="text-xs text-gray-400 mt-2">{series.points.length} samples, {changes.length} change{changes.length === 1 ? '' : 's'}</p>
	{:else}
		<Chart options={chartOptions} />
	{/if}
</div>
